/**
 * 🚌 BusCabinet v1.6.0 (Sovereign Dual-Mode & High-Performance Router)
 * 職責：全自動分片公車站點、路網高畫質渲染、視窗自我調節器、路線交集連動
 */

import { SovereignCabinet } from '../../core/cabinet-base.js';
import { SovereignEvents } from '../../core/cabinet-registry.js';
import { resolveBusRouteColor, chipStyleVars } from '../../utils/bus-route-colors.js';
import {
    resolveBusVehicleTags,
    vehicleBadgesHtml,
    compactVehicleIconsHtml
} from '../../utils/bus-vehicle-tags.js';
import {
    collectStopKeys,
    mergeRoutesForStop,
    dedupeRouteDetailsForDisplay,
    attachStationStopIds
} from '../../utils/bus-stop-routes-merge.js';
import {
    busRouteDetailsMatch,
    busRouteIdentityPayload,
    busRouteStableIds
} from '../../utils/bus-route-identity.js';
import { geometrySourceLabel, isDerivedGeometry } from '../../utils/bus-geometry-source.js';
import { provenanceSummaryZh, routeRoleLabel } from '../../utils/bus-data-provenance.js';
import {
    prepareBusRoutesForDisplay,
    busRouteHasDrawableGeometry
} from '../../utils/bus-route-geometry-sanitize.js';
import { googleMapsNavButtonHtml } from '../../utils/google-maps-nav.js';
import { assignLabelShifts } from '../../utils/bus-map-label-declutter.js';
import { searchBusRoutes, cityCodeFromRoute } from '../../utils/bus-route-search.js';
import { cleanBusRouteRef, isLeakedBusRouteKey } from '../../utils/bus-route-display.js';
import { searchGisPlaces } from '../../utils/gis-place-search.js';
import { appendMapClientQuery, isCompactMapClient, pinnedRouteQueryZoom } from '../../utils/map-client-profile.js';

const BUS_CABINET_UI_REV = '17.0.770-pin-only';
const BUS_LOCKED_POPUP_OPTS = {
    autoClose: false,
    closeOnClick: false,
    autoPan: false,
    keepInView: false,
    closeButton: true,
    closeOnEscapeKey: false
};
const BUS_STOPS_MIN_ZOOM = 12;
const BUS_ROUTES_MIN_ZOOM_COMPACT = 11;
/** 手機指尖約 44px；站牌圖示外框作觸控熱區（路線 canvas 另設 tolerance） */
const BUS_STOP_ICON_SIZE = 28;
const BUS_ROUTE_CANVAS_TOLERANCE = 14;

function busStopDivIcon(active = false) {
    return L.divIcon({
        className: `bus-stop-marker-icon${active ? ' bus-stop-active' : ''}`,
        html: '<span class="bus-stop-dot" aria-hidden="true"></span>',
        iconSize: [BUS_STOP_ICON_SIZE, BUS_STOP_ICON_SIZE],
        iconAnchor: [BUS_STOP_ICON_SIZE / 2, BUS_STOP_ICON_SIZE / 2]
    });
}

class BusCabinet extends SovereignCabinet {
    constructor() {
        super();
        this.loadAssets('/js/cabinets/bus');
        
        // 初始化內部狀態快取
        this.busData = {
            stops: [],
            routes: []
        };
        
        this.stopLayerGroup = null;
        this.routeLayerGroup = null;
        this.activeRouteLayers = {};
        this.activeStopMarkers = {};
        this.highlightedRouteKeys = new Set();
        this.highlightedStopIds = new Set();
        this.busRouteIndex = null;
        /** @type {Map<string, string[]>|null} */
        this.stationGroups = null;
        this.isLoaded = false;
        /** @type {'all'|'single'|'stop'} */
        this.routeFilterMode = 'all';
        /** 是否載入 TDX 營運支線（半線、繞道）；預設僅主線／顯示用站序 */
        this.showRouteVariants = false;
        this.routeFilterDetails = null;
        this.activePopupStop = null;
        this.transportDataUpdatedAt = null;
        this.stopsById = new Map();
        this._viewportReloadTimer = null;
        this._viewportReloading = false;
        this._viewportRoutesTimer = null;
        this._viewportRoutesReloading = false;
        this._routesReloadQueued = false;
        this._stopsReloadQueued = false;
        this._routesFetchGen = 0;
        /** 點站／單線篩選時釘住的完整路線幾何，禁止被 bbox 殘段覆寫 */
        this._pinnedRouteFeatures = [];
        this._clusterLibsLoaded = false;
        this.showStopLabels = false;
        this.showRouteLabels = false;
        this.stopsLayerWanted = false;
        this._activeRoutePopupLayer = null;
        this._lockedMapPopup = null;
        this._lockedPopupKind = null;
        this._ignoreLockedPopupClose = false;
        this._labelDeclutterTimer = null;
        this._routeCatalog = null;
        this._routeCatalogLoading = null;
        this._routeSearchTimer = null;
        this._routeSearchHits = [];
        this._routeSearchActive = -1;
    }

    stopId(stop) {
        const p = stop?.properties || {};
        return p.stop_id || p.id || null;
    }

    routeLayerKeys(props = {}) {
        return busRouteStableIds(props);
    }

    isCompactClient() {
        return isCompactMapClient();
    }

    bboxQuery(bounds) {
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        return appendMapClientQuery(new URLSearchParams({
            xmin: String(sw.lng),
            ymin: String(sw.lat),
            xmax: String(ne.lng),
            ymax: String(ne.lat),
            zoom: String(this.map?.getZoom() ?? '')
        }));
    }

    routesMinZoom() {
        return this.isCompactClient() ? BUS_ROUTES_MIN_ZOOM_COMPACT : 0;
    }

    isRoutesLayerActive() {
        return !!this.shadowRoot?.getElementById('toggleRoutes')?.checked;
    }

    /** 路線圖層開啟時，站↔路線僅承認視窗內已載入之路線幾何 */
    shouldGateRoutesToLoadedGeometry() {
        return this.isRoutesLayerActive();
    }

    stopPropsForRouteLookup(stop) {
        const p = { ...(stop?.properties || {}) };
        attachStationStopIds(p, this.stationGroups);
        return p;
    }

    rawRouteDetailsForStop(stop) {
        const p = this.stopPropsForRouteLookup(stop);
        let list = [];
        if (this.busRouteIndex?.stop_to_routes) {
            list = mergeRoutesForStop(
                this.busRouteIndex.stop_to_routes,
                p,
                this.busRouteIndex.station_to_routes
            );
        }
        // 索引 miss 時回退 API enrich 的 h_route_details（禁止空陣列蓋掉已有關聯）
        if (!list?.length && p.h_route_details?.length) {
            list = [...p.h_route_details];
        }
        return dedupeRouteDetailsForDisplay(list);
    }

    detailMatchesLoadedRoute(detail) {
        if (!detail || !this.busData.routes?.length) return false;
        return this.busData.routes.some((r) =>
            busRouteDetailsMatch(detail, r.properties || {})
        );
    }

    filterDetailsToLoadedRoutes(details) {
        if (!this.shouldGateRoutesToLoadedGeometry()) return details || [];
        if (!this.busData.routes?.length) return [];
        return (details || []).filter((d) => this.detailMatchesLoadedRoute(d));
    }

    routeDetailsForStop(stop) {
        return this.filterDetailsToLoadedRoutes(this.rawRouteDetailsForStop(stop));
    }

    stopHasLoadedRouteAssociation(stop) {
        // 站牌是否顯示與「視窗內路線幾何」解耦；索引／enrich 有路線即顯示
        return this.rawRouteDetailsForStop(stop).length > 0;
    }

    refreshActiveStopPopup() {
        if (this._lockedPopupKind !== 'stop') return;
        if (!this.activePopupStop || !this._lockedMapPopup?.setContent) return;
        this._lockedMapPopup.setContent(this.buildStopPopupHtml(this.activePopupStop));
        this.bindLockedStopPopupHandlers(this._lockedMapPopup.getElement());
        this.setupDraggableBusPopup(this._lockedMapPopup, this._lockedMapPopup.getElement());
    }

    closeLockedMapPopup() {
        if (!this._lockedMapPopup || !this.map) return;
        this._ignoreLockedPopupClose = true;
        try {
            this.teardownDraggableBusPopup(this._lockedMapPopup);
            this.map.closePopup(this._lockedMapPopup);
        } finally {
            this._ignoreLockedPopupClose = false;
            this._lockedMapPopup = null;
            this._lockedPopupKind = null;
        }
    }

    openLockedMapPopup({ latlng, html, className, maxWidth, kind, onReady }) {
        if (!this.map || !latlng) return null;
        this.ensureGlobalPopupStyles();
        this.closeLockedMapPopup();
        const popup = L.popup({
            ...BUS_LOCKED_POPUP_OPTS,
            maxWidth: maxWidth || 310,
            className: className || 'bus-stop-popup'
        }).setLatLng(latlng).setContent(html);

        popup.once('remove', () => {
            this.teardownDraggableBusPopup(popup);
            if (this._ignoreLockedPopupClose) return;
            if (this._lockedMapPopup === popup) {
                this._lockedMapPopup = null;
                this._lockedPopupKind = null;
            }
            this.activePopupStop = null;
            this._activeRoutePopupLayer = null;
        });

        popup.openOn(this.map);
        this._lockedMapPopup = popup;
        this._lockedPopupKind = kind || null;
        const el = popup.getElement();
        if (el) {
            this.setupDraggableBusPopup(popup, el);
            L.DomEvent.disableClickPropagation(el);
            L.DomEvent.disableScrollPropagation(el);
            onReady?.(el, popup);
        }
        return popup;
    }

    displayRouteRef(row = {}) {
        const raw = row.ref || row.h_ref || row.name || row.h_name || '';
        const cleaned = cleanBusRouteRef(raw);
        if (cleaned) return cleaned;
        if (isLeakedBusRouteKey(raw)) {
            const ends = [row.h_departure, row.h_destination].filter(Boolean);
            return ends.length ? ends.join('→') : '未命名';
        }
        return raw || row.route_id || '';
    }

    routeLabelsForStop(stop) {
        const p = stop?.properties || {};
        if (p.h_routes?.length) return p.h_routes;
        return this.routeDetailsForStop(stop).map((r) => this.displayRouteRef(r));
    }

    applyLayerStyle(layer, style) {
        if (!layer) return;
        if (typeof layer.eachLayer === 'function') {
            layer.eachLayer((child) => {
                if (typeof child.setStyle === 'function') child.setStyle(style);
            });
            return;
        }
        if (typeof layer.setStyle === 'function') layer.setStyle(style);
    }

    resolveRouteLayer(detail) {
        if (!detail) return null;
        for (const key of busRouteStableIds(detail)) {
            if (this.activeRouteLayers[key]) return this.activeRouteLayers[key];
        }
        return null;
    }

    registerRouteLayer(polyline, route, props) {
        polyline._busRouteProps = props;
        polyline._busRoute = route;
        for (const key of this.routeLayerKeys(props)) {
            this.activeRouteLayers[key] = polyline;
        }
    }

    openRoutePopup(layer, route, latlng) {
        if (!route) return;
        const at = latlng || layer?.getBounds?.()?.getCenter?.();
        if (!at) return;
        this._activeRoutePopupLayer = layer || null;
        this.openLockedMapPopup({
            latlng: at,
            html: this.buildRoutePopupHtml(route),
            className: 'bus-stop-popup bus-route-popup',
            maxWidth: 320,
            kind: 'route',
            onReady: (_el, popup) => {
                this.refreshRoutePopupTerminals(route, popup).catch((e) => {
                    console.warn('[BusCabinet] terminal name lookup', e);
                });
            }
        });
    }

    routeColor(props = {}) {
        return resolveBusRouteColor(props);
    }

    vehicleTags(props = {}) {
        return resolveBusVehicleTags(props);
    }

    ensureGlobalPopupStyles() {
        const v = BUS_CABINET_UI_REV;
        const sheets = [
            { href: '/css/bus-stop-popup.css', attr: 'data-bus-popup-css' },
            { href: '/css/bus-map-overlays.css', attr: 'data-bus-map-css' }
        ];
        for (const { href, attr } of sheets) {
            if (document.querySelector(`link[${attr}="${v}"]`)) continue;
            document.querySelectorAll(`link[${attr}]`).forEach((el) => el.remove());
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = `${href}?v=${encodeURIComponent(v)}`;
            link.setAttribute(attr, v);
            document.head.appendChild(link);
        }
    }

    blurInteractiveTarget(ev) {
        const t = ev?.originalEvent?.target;
        if (t?.blur) t.blur();
    }

    /** 主權站牌 UID（非人類可讀站名） */
    isStopUidLabel(label) {
        const s = String(label ?? '').trim();
        return /^[A-Z]{2,5}\d{3,}$/i.test(s);
    }

    resolveStopName(stopId) {
        const id = String(stopId ?? '');
        const feat = this.stopsById.get(id);
        if (feat?.properties) {
            return feat.properties.h_stop_name || feat.properties.name || id;
        }
        const cached = this._stopNameById?.get(id);
        if (cached) return cached;
        return id;
    }

    pickTerminalLabel(metaName, resolvedFromStopId) {
        const meta = String(metaName ?? '').trim();
        if (meta) return meta;
        const resolved = String(resolvedFromStopId ?? '').trim();
        if (resolved && !this.isStopUidLabel(resolved)) return resolved;
        return resolved || '—';
    }

    getRouteTerminals(route) {
        const p = route?.properties || {};
        const routeId = p.route_id || p.id || p.h_route_key;
        const indexMeta = routeId ? this.busRouteIndex?.route_to_stops?.[routeId] : null;

        const depMeta =
            p.h_departure ||
            p.departure ||
            indexMeta?.h_departure ||
            indexMeta?.departure_label ||
            '';
        const destMeta =
            p.h_destination ||
            p.destination ||
            indexMeta?.h_destination ||
            indexMeta?.destination_label ||
            '';

        let ids = p.stop_ids || p.h_stop_ids;
        if (!ids?.length && indexMeta?.stop_ids) ids = indexMeta.stop_ids;
        if (!ids?.length) {
            return {
                first: depMeta || '—',
                last: destMeta || '—',
                terminalStopIds: []
            };
        }

        const terminalStopIds = [ids[0], ids[ids.length - 1]].filter(Boolean);
        return {
            first: this.pickTerminalLabel(depMeta, this.resolveStopName(ids[0])),
            last: this.pickTerminalLabel(destMeta, this.resolveStopName(ids[ids.length - 1])),
            terminalStopIds
        };
    }

    async fetchStopNamesByIds(ids) {
        const need = [...new Set((ids || []).map(String).filter((id) => id && this.isStopUidLabel(id)))];
        if (!need.length) return {};
        const q = new URLSearchParams({ ids: need.join(',') });
        const res = await fetch(`/api/live/bus/stops/names?${q}`, { credentials: 'include' });
        if (!res.ok) return {};
        const data = await res.json();
        if (!this._stopNameById) this._stopNameById = new Map();
        for (const [id, name] of Object.entries(data.names || {})) {
            if (name) this._stopNameById.set(String(id), name);
        }
        return data.names || {};
    }

    async refreshRoutePopupTerminals(route, popup) {
        const el = popup?.getElement?.();
        if (!el) return;
        const { first, last, terminalStopIds } = this.getRouteTerminals(route);
        if (!this.isStopUidLabel(first) && !this.isStopUidLabel(last)) return;
        const names = await this.fetchStopNamesByIds(terminalStopIds);
        const rowFirst = el.querySelector('[data-terminal="first"]');
        const rowLast = el.querySelector('[data-terminal="last"]');
        if (rowFirst && names[terminalStopIds[0]]) rowFirst.textContent = names[terminalStopIds[0]];
        if (rowLast && names[terminalStopIds[terminalStopIds.length - 1]]) {
            rowLast.textContent = names[terminalStopIds[terminalStopIds.length - 1]];
        }
    }

    buildStopMapLabelHtml(stop) {
        const p = stop?.properties || {};
        const name = this.escapeHtml(p.h_stop_name || p.name || '站牌');
        const tags = this.vehicleTags(p);
        return `<span class="bus-map-label-text">${name}</span>${compactVehicleIconsHtml(tags)}`;
    }

    buildRouteMapLabelHtml(props = {}) {
        const label = this.escapeHtml(this.displayRouteRef(props) || '路線');
        const tags = this.vehicleTags(props);
        return `<span class="bus-map-label-text">${label}</span>${compactVehicleIconsHtml(tags)}`;
    }

    /**
     * 依縮放與幾何來源統一線型（雙北 TDX_STOP_CHAIN 在遠距離需加粗，避免看起來像沒資料）
     */
    getRouteLineStyle(props, zoom = null) {
        const z = zoom ?? this.map?.getZoom?.() ?? 12;
        const derived = isDerivedGeometry(props);
        const color = this.routeColor(props);

        if (derived) {
            if (z <= 8) {
                return { color, weight: 5.5, opacity: 0.88, dashArray: '4 3', lineJoin: 'round' };
            }
            if (z <= 10) {
                return { color, weight: 4.5, opacity: 0.8, dashArray: '6 4', lineJoin: 'round' };
            }
            return { color, weight: 3.5, opacity: 0.68, dashArray: '8 6', lineJoin: 'round' };
        }

        if (z <= 8) {
            return { color, weight: 5.5, opacity: 0.92, dashArray: null, lineJoin: 'round', lineCap: 'butt' };
        }
        if (z <= 10) {
            return { color, weight: 5, opacity: 0.88, dashArray: null, lineJoin: 'round' };
        }
        return { color, weight: 4.5, opacity: 0.85, dashArray: null, lineJoin: 'round' };
    }

    restyleRoutesForZoom() {
        if (!this.routeLayerGroup || !this.map) return;
        const z = this.map.getZoom();
        this.routeLayerGroup.eachLayer((layer) => {
            const props = layer._busRouteProps
                || layer.feature?.properties
                || layer.options?.busRouteProps;
            if (!props || !this.routePropsMatchesFilter(props)) return;
            this.applyLayerStyle(layer, this.getRouteLineStyle(props, z));
        });
    }

    buildRoutePopupHtml(route) {
        const p = route?.properties || {};
        const routeLabel = this.escapeHtml(this.displayRouteRef(p) || '路線');
        const { first, last } = this.getRouteTerminals(route);
        const tags = this.vehicleTags(p);
        const badges = vehicleBadgesHtml(tags);
        const updated = this.escapeHtml(
            this.formatMotcUpdatedAt({ properties: p })
        );
        const geomSrc = p.h_geometry_source
            ? this.escapeHtml(geometrySourceLabel(p.h_geometry_source))
            : '無路線軌跡';
        const derivedHint = isDerivedGeometry(p)
            ? '<div style="font-size:11px;color:#fbbf24;margin-top:6px;">⚠ 路線為推算／備援幾何，實際行駛路徑可能不同</div>'
            : '';

        return `<div class="bus-stop-popup-inner bus-route-popup-inner" data-bus-ui-rev="${BUS_CABINET_UI_REV}">
            <div class="bus-stop-popup-drag-handle" data-bus-drag-handle title="按住拖曳移動彈窗">
                <div class="bus-stop-popup-kicker">🚌 公車路線 <span class="bus-stop-popup-drag-hint">⠿ 可拖曳 · 已鎖定 · 點 × 關閉</span></div>
                <div class="bus-stop-popup-title">${routeLabel}</div>
            </div>
            <div class="bus-stop-popup-label">首末站</div>
            <div style="font-size:13px;color:#e2e8f0;line-height:1.5;margin-bottom:8px;">
                <div><span style="color:#94a3b8;">起點：</span><span data-terminal="first">${this.escapeHtml(first)}</span></div>
                <div><span style="color:#94a3b8;">迄點：</span><span data-terminal="last">${this.escapeHtml(last)}</span></div>
            </div>
            <div class="bus-stop-popup-label">車型標記</div>
            <div style="margin-bottom:8px;">${badges || '<span style="color:#64748b;">—</span>'}</div>
            <div class="bus-stop-popup-source">
                資料來源：${this.escapeHtml(provenanceSummaryZh(p.h_data_provenance))}<br>
                路線類型：${this.escapeHtml(routeRoleLabel(p.h_route_role))}${p.h_variant_kind ? `（${this.escapeHtml(p.h_variant_kind)}）` : ''}<br>
                路線軌跡：${geomSrc}<br>
                資料最後更新：${updated}
                ${derivedHint}<br>
                <span style="opacity:0.55;">介面 ${BUS_CABINET_UI_REV}</span>
            </div>
        </div>`;
    }

    bindMapLabel(layer, html, enabled) {
        if (!layer) return;
        if (!enabled) {
            if (layer.getTooltip?.()) layer.unbindTooltip();
            return;
        }
        layer.bindTooltip(html, {
            permanent: true,
            direction: 'top',
            offset: [0, -10],
            className: 'bus-map-label',
            opacity: 1,
            interactive: false
        });
        layer.openTooltip();
        this.scheduleLabelDeclutter();
    }

    scheduleLabelDeclutter() {
        clearTimeout(this._labelDeclutterTimer);
        this._labelDeclutterTimer = setTimeout(() => this.applyLabelDeclutter(), 40);
    }

    collectLabelTooltipLayers() {
        const layers = [];
        const walk = (layer) => {
            if (!layer) return;
            if (typeof layer.eachLayer === 'function' && !layer.getTooltip?.()) {
                layer.eachLayer(walk);
                return;
            }
            if (layer.getTooltip?.()) layers.push(layer);
            else if (typeof layer.eachLayer === 'function') layer.eachLayer(walk);
        };
        this.stopLayerGroup?.eachLayer(walk);
        this.routeLayerGroup?.eachLayer(walk);
        return layers;
    }

    applyLabelDeclutter() {
        const layers = this.collectLabelTooltipLayers();
        const items = [];
        for (const layer of layers) {
            const el = layer.getTooltip?.()?.getElement?.();
            if (!el) continue;
            el.classList.remove('bus-map-label-hidden');
            el.style.marginLeft = '';
            el.style.marginTop = '';
            const r = el.getBoundingClientRect();
            items.push({ layer, el, left: r.left, top: r.top, width: r.width, height: r.height });
        }
        const assigned = assignLabelShifts(items.map(({ left, top, width, height }) => ({
            left, top, width, height
        })));
        assigned.forEach((a, i) => {
            const el = items[i].el;
            if (a.hidden) {
                el.classList.add('bus-map-label-hidden');
                el.style.marginLeft = '';
                el.style.marginTop = '';
                return;
            }
            el.classList.remove('bus-map-label-hidden');
            el.style.marginLeft = a.dx ? `${a.dx}px` : '';
            el.style.marginTop = a.dy ? `${a.dy}px` : '';
        });
    }

    syncStopLabelsVisibility() {
        if (!this.stopLayerGroup) return;
        this.stopLayerGroup.eachLayer((marker) => {
            const stop = marker._busStop;
            if (!stop) return;
            this.bindMapLabel(
                marker,
                this.buildStopMapLabelHtml(stop),
                this.showStopLabels
            );
        });
    }

    syncRouteLabelsVisibility() {
        if (!this.routeLayerGroup) return;
        this.routeLayerGroup.eachLayer((layer) => {
            const props = layer._busRouteProps || layer.feature?.properties;
            if (!props) return;
            this.bindMapLabel(
                layer,
                this.buildRouteMapLabelHtml(props),
                this.showRouteLabels
            );
        });
    }

    syncMapLabelsVisibility() {
        this.syncStopLabelsVisibility();
        this.syncRouteLabelsVisibility();
        this.scheduleLabelDeclutter();
    }

    updateLabelToggleStates() {
        const shadow = this.shadowRoot;
        const toggleStops = shadow?.getElementById('toggleStops');
        const toggleRoutes = shadow?.getElementById('toggleRoutes');
        const toggleStopLabels = shadow?.getElementById('toggleStopLabels');
        const toggleRouteLabels = shadow?.getElementById('toggleRouteLabels');
        if (toggleStopLabels) {
            toggleStopLabels.disabled = !toggleStops?.checked;
            if (!toggleStops?.checked) {
                toggleStopLabels.checked = false;
                this.showStopLabels = false;
            }
        }
        if (toggleRouteLabels) {
            toggleRouteLabels.disabled = !toggleRoutes?.checked;
            if (!toggleRoutes?.checked) {
                toggleRouteLabels.checked = false;
                this.showRouteLabels = false;
            }
        }
    }

    setRouteLayerInteractive(layer, interactive) {
        if (!layer) return;
        const apply = (child) => {
            child.options.interactive = interactive;
            // SVG 路徑；Canvas 無 _path，靠 options.interactive 在 _onClick 時過濾
            if (child._path) {
                child._path.style.pointerEvents = interactive ? '' : 'none';
            }
        };
        if (typeof layer.eachLayer === 'function') layer.eachLayer(apply);
        else apply(layer);
    }

    setStopMarkerVisible(marker, visible, active = false) {
        if (!marker) return;
        marker.options.interactive = !!visible;
        if (typeof marker.setOpacity === 'function') {
            marker.setOpacity(visible ? 1 : 0);
        }
        if (typeof marker.setIcon === 'function') {
            marker.setIcon(busStopDivIcon(!!active && visible));
        }
        const el = marker.getElement?.() || marker._icon;
        if (el) {
            el.style.pointerEvents = visible ? 'auto' : 'none';
            el.classList.toggle('bus-stop-active', !!(active && visible));
            el.classList.toggle('bus-stop-hidden', !visible);
        }
    }

    escapeHtml(text) {
        return String(text ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    formatMotcUpdatedAt(stop) {
        const ts = stop?.properties?._source_info?.ts
            || this.transportDataUpdatedAt
            || this.busRouteIndex?.generated_at;
        if (!ts) return '待編譯更新';
        try {
            return new Date(ts).toLocaleDateString('zh-TW', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
        } catch {
            return String(ts).slice(0, 10);
        }
    }

    detailMatchesFilter(detail) {
        if (this.routeFilterMode === 'all' || !detail) return true;
        if (this.routeFilterMode === 'single' && this.routeFilterDetails?.length) {
            return busRouteDetailsMatch(detail, this.routeFilterDetails[0]);
        }
        if (this.routeFilterMode === 'stop' && this.routeFilterDetails?.length) {
            return this.routeFilterDetails.some((t) => busRouteDetailsMatch(detail, t));
        }
        return true;
    }

    routePropsMatchesFilter(props) {
        return this.detailMatchesFilter(busRouteIdentityPayload(props));
    }

    /**
     * 直接確保路線圖層就緒，不走 checkbox change 事件（避免時序競爭）
     */
    isPinnedRouteFilter() {
        return this.routeFilterMode !== 'all' && !!this.routeFilterDetails?.length;
    }

    async enableRoutesLayer() {
        const toggleRoutes = this.shadowRoot?.getElementById('toggleRoutes');
        if (toggleRoutes) {
            // 先移除 change 監聽器防止觸發 renderAllRoutes 競爭
            this._suppressRouteToggle = true;
            toggleRoutes.checked = true;
            this._suppressRouteToggle = false;
        }
        if (this.isPinnedRouteFilter()) {
            await this.paintPinnedRoutes();
            return;
        }
        await this.ensureRoutesLoaded();
    }

    /** 卸掉 bbox 總覽網，只畫 by-ids 釘住的線 */
    async paintPinnedRoutes() {
        this._routesFetchGen = (this._routesFetchGen || 0) + 1;
        await this.ensurePinnedFilterRoutes();
        if (this._pinnedRouteFeatures.length) {
            this.busData.routes = [...this._pinnedRouteFeatures];
        }
        this.activeRouteLayers = {};
        this.routeLayerGroup?.clearLayers?.();
        await this.renderAllRoutes();
        const n = this.busData.routes.length;
        this.setStatus(n ? `✅ ${n} 條所選路線（未載入總覽網）` : '找不到該路線幾何');
    }

    /**
     * 依 routeFilterMode / routeFilterDetails 套用可見度（遍歷實際圖層，非僅 busData 快取）
     */
    applyRouteVisibilityFilter() {
        if (!this.routeLayerGroup) return;

        const applyToLayer = (layer, props) => {
            if (!layer || !props) return;
            const color = this.routeColor(props);
            const visible = this.routePropsMatchesFilter(props);
            if (visible) {
                this.setRouteLayerInteractive(layer, true);
                if (typeof layer.setOpacity === 'function') layer.setOpacity(1);
                const base = this.getRouteLineStyle(props);
                this.applyLayerStyle(layer, {
                    ...base,
                    weight: this.routeFilterMode === 'all' ? base.weight : base.weight + 1.5,
                    opacity: Math.min(1, base.opacity + 0.05)
                });
            } else {
                this.setRouteLayerInteractive(layer, false);
                if (typeof layer.setOpacity === 'function') layer.setOpacity(0);
                this.applyLayerStyle(layer, {
                    color: '#475569',
                    weight: 0,
                    opacity: 0,
                    dashArray: null
                });
            }
        };

        let styled = 0;
        this.routeLayerGroup.eachLayer((layer) => {
            const props = layer._busRouteProps
                || layer.feature?.properties
                || layer.options?.busRouteProps;
            if (!props) return;
            styled += 1;
            applyToLayer(layer, props);
        });

        if (!styled && this.busData.routes.length) {
            this.busData.routes.forEach((route) => {
                const layer = this.resolveRouteLayer(route.properties);
                applyToLayer(layer, route.properties);
            });
        }
    }

    clearRouteFilter() {
        this.routeFilterMode = 'all';
        this.routeFilterDetails = null;
        this._pinnedRouteFeatures = [];
        this.highlightedStopIds.clear();
        if (this._activeRoutePopupLayer?.closePopup) {
            this._activeRoutePopupLayer.closePopup();
        }
        this._activeRoutePopupLayer = null;
        this.resetRouteHighlights();
        this.busData.routes = [];
        this._routesFetchGen = (this._routesFetchGen || 0) + 1;
        this.reloadRoutesForViewport().catch((e) => console.warn('[BusCabinet] clear filter reload', e));
    }

    /** 依目前路線篩選收集應顯示的站牌 ID（含 stop_ids 別名） */
    collectStopIdsForRouteDetails(details = []) {
        const ids = new Set();
        const index = this.busRouteIndex?.route_to_stops;
        for (const detail of details) {
            const routeId = detail?.route_id;
            if (routeId && index?.[routeId]?.stop_ids?.length) {
                for (const id of index[routeId].stop_ids) ids.add(String(id));
            } else if (index) {
                for (const [rid, meta] of Object.entries(index)) {
                    if (!busRouteDetailsMatch(detail, {
                        route_id: rid,
                        ref: meta.ref,
                        name: meta.name,
                        h_city_code: meta.h_city_code,
                        h_route_family_id: meta.h_route_family_id,
                        tdx_route_uid: meta.tdx_route_uid
                    })) {
                        continue;
                    }
                    for (const id of meta.stop_ids || []) ids.add(String(id));
                }
            }
            for (const route of this.busData.routes || []) {
                if (!busRouteDetailsMatch(route.properties, detail)) continue;
                for (const id of route.properties?.stop_ids || []) {
                    ids.add(String(id));
                }
            }
        }
        return ids;
    }

    /** @returns {Set<string>|null} null 表示不篩選（全部顯示） */
    getVisibleStopIdsForFilter() {
        if (this.routeFilterMode === 'all' || !this.routeFilterDetails?.length) return null;
        return this.collectStopIdsForRouteDetails(this.routeFilterDetails);
    }

    stopMatchesRouteFilter(stop) {
        const visibleIds = this.getVisibleStopIdsForFilter();
        if (!visibleIds) return true;
        return collectStopKeys(stop?.properties || {}).some((k) => visibleIds.has(k));
    }

    applyStopVisibilityFilter() {
        if (!this.stopLayerGroup) return;
        const visibleIds = this.getVisibleStopIdsForFilter();
        const activeKeys = this.activePopupStop
            ? new Set(collectStopKeys(this.activePopupStop.properties))
            : null;

        this.stopLayerGroup.eachLayer((marker) => {
            const stop = marker._busStop;
            const keys = stop ? collectStopKeys(stop.properties) : [];
            const visible = !visibleIds || keys.some((k) => visibleIds.has(k));
            const isActive = !!(activeKeys && keys.some((k) => activeKeys.has(k)));
            this.setStopMarkerVisible(marker, visible, isActive);
        });
    }

    /**
     * 只顯示單條路線（其餘淡化）
     * 關鍵修正：先設定 filter 狀態，再觸發渲染，確保 renderAllRoutes 結束時套用正確樣式
     */
    expandRouteFilterDetails(detail) {
        const payload = busRouteIdentityPayload(detail);
        const city = payload.h_city_code || cityCodeFromRoute(payload);
        const ref = String(payload.ref || '');
        const family = payload.h_route_family_id;
        const siblings = (this._routeCatalog || []).filter((r) => {
            if (family && r.h_route_family_id === family) return true;
            return city && ref && r.h_city_code === city && String(r.ref) === ref;
        });
        if (!siblings.length) return [payload];
        return siblings.map((r) => busRouteIdentityPayload(r));
    }

    async showOnlyRoute(detail, stop) {
        this.isLoaded = true;
        // ① 先設定狀態（不依目前 bbox 閘住；整條線改由 by-ids 釘住）
        this.routeFilterMode = 'single';
        this.routeFilterDetails = this.expandRouteFilterDetails(detail);
        this.activePopupStop = stop;
        this.highlightedRouteKeys = new Set(
            this.routeFilterDetails.flatMap((d) => [...busRouteStableIds(d)])
        );
        this.highlightedStopIds = this.collectStopIdsForRouteDetails(this.routeFilterDetails);
        if (stop) {
            for (const k of collectStopKeys(stop.properties)) this.highlightedStopIds.add(k);
        }

        await this.enableRoutesLayer();
        this.applyStopVisibilityFilter();

        const layer = this.resolveRouteLayer(detail);
        if (layer) {
            this.applyLayerStyle(layer, {
                color: this.routeColor(detail),
                weight: 8,
                opacity: 1,
                dashArray: null
            });
        }
        this.refreshPopupActiveChips();
    }

    /**
     * 只顯示此站所有行經路線（其餘淡化）
     * 必須拉整條線幾何，禁止只留當前 bbox 殘段。
     */
    async showStopRelatedRoutes(stop) {
        const sid = this.stopId(stop);
        let details = this.rawRouteDetailsForStop(stop);
        if (!details.length && sid) details = await this.fetchStopRoutes(sid);
        details = dedupeRouteDetailsForDisplay(details || []);
        if (!details.length) return;

        // ① 先設定狀態
        this.routeFilterMode = 'stop';
        this.routeFilterDetails = details;
        this.activePopupStop = stop;
        this.highlightedRouteKeys = new Set(
            details.flatMap((d) => [...busRouteStableIds(d)])
        );
        this.highlightedStopIds = this.collectStopIdsForRouteDetails(details);
        if (sid) this.highlightedStopIds.add(String(sid));
        for (const k of collectStopKeys(stop?.properties || {})) {
            this.highlightedStopIds.add(k);
        }

        await this.enableRoutesLayer();
        this.applyStopVisibilityFilter();
        this.refreshPopupActiveChips();
    }

    routeFeatureKey(props = {}) {
        return String(props.route_id || props.ref || props.id || props.name || '');
    }

    mergeRouteFeatures(existing = [], incoming = []) {
        const map = new Map();
        for (const f of existing) {
            const k = this.routeFeatureKey(f.properties || {});
            if (k) map.set(k, f);
        }
        for (const f of incoming) {
            const k = this.routeFeatureKey(f.properties || {});
            if (k) map.set(k, f);
        }
        return [...map.values()];
    }

    async fetchRoutesByIds(ids = []) {
        const uniq = [...new Set(ids.map((id) => String(id || '').trim()).filter(Boolean))].slice(0, 120);
        if (!uniq.length) return [];
        const q = appendMapClientQuery(new URLSearchParams({ ids: uniq.join(',') }));
        if (this.showRouteVariants) q.set('variants', '1');
        if (this.map) q.set('zoom', String(pinnedRouteQueryZoom(this.map.getZoom())));
        const gen = this._routesFetchGen;
        const res = await fetch(`/api/live/bus/routes/by-ids?${q}`, { credentials: 'include' });
        if (gen !== this._routesFetchGen) return [];
        if (!res.ok) return [];
        const data = await res.json();
        return prepareBusRoutesForDisplay(data.features || []);
    }

    async ensurePinnedFilterRoutes() {
        if (this.routeFilterMode === 'all' || !this.routeFilterDetails?.length) return;
        const ids = this.routeFilterDetails.flatMap((d) => [
            d.route_id,
            d.id,
            d.h_route_key,
            d.h_uid,
            d.h_route_family_id,
            d.tdx_route_uid,
            d.h_ref_codes?.tdx_route_uid,
            d.tdx_sub_route_uid,
            d.h_ref_codes?.tdx_sub_route_uid
        ]).filter(Boolean);
        const fetched = await this.fetchRoutesByIds(ids);
        if (fetched.length) {
            this._pinnedRouteFeatures = fetched;
        }
        if (this._pinnedRouteFeatures.length) {
            this.busData.routes = [...this._pinnedRouteFeatures];
        }
    }

    refreshPopupActiveChips() {
        if (!this.activePopupStop) return;
        const popupEl = this._lockedMapPopup?.getElement?.();
        if (!popupEl) return;
        const details = this.routeDetailsForStop(this.activePopupStop);
        popupEl.querySelectorAll('.bus-route-chip[data-route-id]').forEach((chip) => {
            const rid = chip.getAttribute('data-route-id');
            const detail = details.find((r) =>
                String(r.route_id) === rid || String(r.ref) === rid
            );
            const active = this.routeFilterMode === 'single'
                && detail
                && this.detailMatchesFilter(detail);
            chip.classList.toggle('is-active', !!active);
        });
    }

    buildStopPopupHtml(stop) {
        const p = stop.properties || {};
        const stopName = this.escapeHtml(p.h_stop_name || p.name || '未命名站牌');
        const details = this.rawRouteDetailsForStop(stop);
        const chips = details.length
            ? details
            : this.routeLabelsForStop(stop).map((label) => ({ ref: label }));

        const routesList = chips.length
            ? chips.map((d) => {
                const label = this.escapeHtml(this.displayRouteRef(d));
                const hex = this.routeColor(d);
                const tags = this.vehicleTags(d);
                const badges = vehicleBadgesHtml(tags);
                const rid = this.escapeHtml(String(d.route_id || d.ref || label));
                return `<div class="bus-route-row">
                <button type="button" class="bus-route-chip" data-route-id="${rid}" style="${chipStyleVars(hex)}">${label}</button>
                ${badges}
            </div>`;
            }).join('')
            : (this.shouldGateRoutesToLoadedGeometry()
                ? '<span style="color:#64748b;font-size:12px;line-height:1.5;">此視窗內無已載入路線經過此站。<br>請確認已開啟「顯示全部路線」，或平移／縮放地圖以載入該線。</span>'
                : '<span style="color:#64748b;">無行經路線</span>');

        const updated = this.escapeHtml(this.formatMotcUpdatedAt(stop));
        const navBtn = googleMapsNavButtonHtml(stop);

        return `<div class="bus-stop-popup-inner" data-bus-ui-rev="${BUS_CABINET_UI_REV}">
            <div class="bus-stop-popup-drag-handle" data-bus-drag-handle title="按住拖曳移動彈窗">
                <div class="bus-stop-popup-kicker">🚌 公車站牌 <span class="bus-stop-popup-drag-hint">⠿ 可拖曳 · 已鎖定 · 點 × 關閉</span></div>
                <div class="bus-stop-popup-title">${stopName}</div>
                ${navBtn}
            </div>
            <div class="bus-stop-popup-label" style="font-size:12px;font-weight:600;color:#cbd5e1;margin-bottom:6px;">行經路線（點擊僅顯示該線）：</div>
            <div class="bus-stop-popup-routes">${routesList}</div>
            <div class="bus-stop-popup-actions">
                <button type="button" class="bus-stop-popup-btn primary" data-bus-show-all>顯示此站全部路線</button>
                <button type="button" class="bus-stop-popup-btn" data-bus-clear-filter>恢復顯示視窗路線</button>
            </div>
            <div class="bus-stop-popup-source" style="margin-top:8px;font-size:10px;color:#94a3b8;line-height:1.45;">
                來源：中華民國交通部（TDX 運輸資料流通服務）<br>
                資料最後更新：${updated}<br>
                <span style="opacity:0.55;">介面 ${BUS_CABINET_UI_REV}</span>
            </div>
        </div>`;
    }

    patchPopupPositionForDrag(popup) {
        if (!popup || popup._busPositionPatch) return;
        popup._busPositionPatch = true;
        popup._busOrigUpdatePosition = popup._updatePosition.bind(popup);
        popup._updatePosition = function busPopupUpdatePosition() {
            if (this._busDragged) return;
            return this._busOrigUpdatePosition();
        };
    }

    /** 讀取 Leaflet popup 目前的 translate3d，避免改 left/top 造成座標系跳動 */
    readPopupTranslate(el) {
        const tr = window.getComputedStyle(el).transform;
        if (!tr || tr === 'none') return { x: 0, y: 0 };
        if (tr.startsWith('matrix3d(')) {
            const parts = tr.slice(9, -1).split(',').map((v) => parseFloat(v.trim()));
            return { x: parts[12] || 0, y: parts[13] || 0 };
        }
        if (tr.startsWith('matrix(')) {
            const parts = tr.slice(7, -1).split(',').map((v) => parseFloat(v.trim()));
            return { x: parts[4] || 0, y: parts[5] || 0 };
        }
        const m = tr.match(/translate3d\(([-\d.]+)px,\s*([-.\d]+)px/);
        if (m) return { x: parseFloat(m[1]), y: parseFloat(m[2]) };
        return { x: 0, y: 0 };
    }

    setupDraggableBusPopup(popup, rootEl) {
        if (!popup || !rootEl || !this.map) return;

        const popupPane = rootEl.classList?.contains('leaflet-popup')
            ? rootEl
            : rootEl.closest('.leaflet-popup');
        const handle = rootEl.querySelector('[data-bus-drag-handle]');
        if (!popupPane || !handle || handle._busDragSetup) return;

        this.patchPopupPositionForDrag(popup);
        handle._busDragSetup = true;

        const map = this.map;

        const endDrag = () => {
            if (!popup._busDragSession) return;
            const s = popup._busDragSession;
            if (!s.moved) popup._busDragged = false;
            map.dragging.enable();
            popup._busDragSession = null;
            popupPane.classList.remove('bus-stop-popup-dragging');
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
        };

        const applyDelta = (dx, dy) => {
            const s = popup._busDragSession;
            if (!s) return;
            if (Math.abs(dx) > 2 || Math.abs(dy) > 2) s.moved = true;
            popup._busDragged = true;
            popupPane.classList.add('is-dragged');
            const x = Math.round(s.originX + dx);
            const y = Math.round(s.originY + dy);
            popupPane.style.transform = `translate3d(${x}px, ${y}px, 0px)`;
        };

        const startDrag = (clientX, clientY) => {
            const origin = this.readPopupTranslate(popupPane);
            popup._busDragSession = {
                startX: clientX,
                startY: clientY,
                originX: origin.x,
                originY: origin.y,
                moved: false
            };
            popup._busDragged = true;
            map.dragging.disable();
            popupPane.classList.add('bus-stop-popup-dragging');
        };

        const onMove = (e) => {
            const s = popup._busDragSession;
            if (!s) return;
            L.DomEvent.preventDefault(e);
            applyDelta(e.clientX - s.startX, e.clientY - s.startY);
        };

        const onUp = () => endDrag();

        const onTouchMove = (e) => {
            const s = popup._busDragSession;
            const t = e.touches[0];
            if (!s || !t) return;
            L.DomEvent.preventDefault(e);
            applyDelta(t.clientX - s.startX, t.clientY - s.startY);
        };

        const onTouchEnd = () => endDrag();

        const onHandleDown = (e) => {
            if (e.type === 'mousedown' && e.button !== 0) return;
            L.DomEvent.stopPropagation(e);
            L.DomEvent.preventDefault(e);
            const pt = e.touches?.[0] || e;
            startDrag(pt.clientX, pt.clientY);
            if (e.type === 'mousedown') {
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
            } else {
                document.addEventListener('touchmove', onTouchMove, { passive: false });
                document.addEventListener('touchend', onTouchEnd);
            }
        };

        handle.addEventListener('mousedown', onHandleDown);
        handle.addEventListener('touchstart', onHandleDown, { passive: false });

        popup._busDragTeardown = () => {
            endDrag();
            handle.removeEventListener('mousedown', onHandleDown);
            handle.removeEventListener('touchstart', onHandleDown);
            handle._busDragSetup = false;
            popup._busDragged = false;
            popupPane.classList.remove('is-dragged', 'bus-stop-popup-dragging');
            popupPane.style.transform = '';
            if (!popup._busDragged && popup._busOrigUpdatePosition) {
                popup._busOrigUpdatePosition();
            }
        };
    }

    teardownDraggableBusPopup(popup) {
        if (!popup) return;
        popup._busDragTeardown?.();
        popup._busDragTeardown = null;
    }

    bindLockedStopPopupHandlers(el) {
        if (!el || el._busStopHandlersBound) return;
        el._busStopHandlersBound = true;
        el.addEventListener('click', (ev) => {
            const stop = this.activePopupStop;
            if (!stop) return;
            const chip = ev.target.closest('.bus-route-chip[data-route-id]');
            if (chip) {
                ev.preventDefault();
                ev.stopPropagation();
                const rid = chip.getAttribute('data-route-id');
                const detail = this.routeDetailsForStop(stop).find((r) =>
                    String(r.route_id) === rid || String(r.ref) === rid
                );
                if (detail) this.showOnlyRoute(detail, stop);
                return;
            }
            if (ev.target.closest('[data-bus-show-all]')) {
                ev.preventDefault();
                ev.stopPropagation();
                this.showStopRelatedRoutes(stop);
                return;
            }
            if (ev.target.closest('[data-bus-clear-filter]')) {
                ev.preventDefault();
                ev.stopPropagation();
                this.clearRouteFilter();
            }
        });
    }

    openLockedStopPopup(stop, latlng) {
        if (!stop || !latlng) return;
        this.activePopupStop = stop;
        this.openLockedMapPopup({
            latlng,
            html: this.buildStopPopupHtml(stop),
            className: 'bus-stop-popup',
            maxWidth: 310,
            kind: 'stop',
            onReady: (el) => this.bindLockedStopPopupHandlers(el)
        });
    }

    bindStopPopupInteractions(marker, stop) {
        marker._busStop = stop;
        if (marker._busPopupBound) return;
        marker._busPopupBound = true;
        marker.on('click', (e) => {
            L.DomEvent.stopPropagation(e);
            this.openLockedStopPopup(stop, e.latlng || marker.getLatLng());
        });
    }

    async fetchStopRoutes(stopId) {
        if (!stopId) return [];
        try {
            const res = await fetch(`/api/live/bus/stop/${encodeURIComponent(stopId)}/routes`, {
                credentials: 'include'
            });
            if (res.ok) {
                const data = await res.json();
                return data.routes || [];
            }
        } catch (e) {
            console.warn('[BusCabinet] stop routes fetch failed', e);
        }
        return [];
    }

    afterAssetsMounted() {
        if (this.map && this.isRoutesLayerActive()) {
            this.scheduleViewportRoutesReload();
        }
    }

    onMount() {
        this.ensureGlobalPopupStyles();
        console.info(`[BusCabinet] UI ${BUS_CABINET_UI_REV} loaded`);
        this.initControls();
        
        // 分時圈只繪圈，不連動開啟公車路線圖層（會載入全視窗路線、易當機）
        if (this.bus) {
            this.bus.on(SovereignEvents.ISOC_RENDERED, () => {
                /* 刻意 no-op：高亮／收割僅在分時圈柜／匯出內處理 */
            });
            this.bus.on(SovereignEvents.ISOC_CLEARED, () => {
                this.highlightedStopIds.clear();
                this.resetRouteHighlights();
                this.reapplyStopHighlights();
            });
        }
    }

    onUnmount() {
        this.clearAll();
    }

    ensureMarkerClusterLib() {
        if (this._clusterLibsLoaded && typeof L?.markerClusterGroup === 'function') {
            return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
            if (!document.querySelector('link[data-bus-mc-css="1"]')) {
                for (const href of [
                    '/lib/markercluster/MarkerCluster.css',
                    '/lib/markercluster/MarkerCluster.Default.css'
                ]) {
                    const link = document.createElement('link');
                    link.rel = 'stylesheet';
                    link.href = href;
                    link.setAttribute('data-bus-mc-css', '1');
                    document.head.appendChild(link);
                }
            }
            if (typeof L?.markerClusterGroup === 'function') {
                this._clusterLibsLoaded = true;
                resolve();
                return;
            }
            const script = document.createElement('script');
            script.src = '/lib/markercluster/leaflet.markercluster.js';
            script.onload = () => {
                this._clusterLibsLoaded = true;
                resolve();
            };
            script.onerror = () => reject(new Error('markercluster load failed'));
            document.head.appendChild(script);
        });
    }

    async onMapReady(map) {
        this.map = map;

        await this.ensureMarkerClusterLib().catch((e) =>
            console.warn('[BusCabinet] markercluster unavailable', e)
        );

        if (typeof L?.markerClusterGroup === 'function') {
            this.stopLayerGroup = L.markerClusterGroup({
                disableClusteringAtZoom: 15,
                maxClusterRadius: 55,
                showCoverageOnHover: false,
                spiderfyOnMaxZoom: true
            }).addTo(this.map);
        } else {
            this.stopLayerGroup = L.layerGroup().addTo(this.map);
        }
        this.routeLayerGroup = L.layerGroup().addTo(this.map);

        this.loadBusNetwork();

        const onViewportChange = () => {
            this.scheduleViewportStopsReload();
            this.scheduleViewportRoutesReload();
        };
        const onZoomStyle = () => {
            this.restyleRoutesForZoom();
        };
        this.map.on('moveend', onViewportChange);
        this.map.on('zoomend', () => {
            onViewportChange();
            onZoomStyle();
        });
    }

    scheduleViewportRoutesReload() {
        if (!this.isLoaded || !this.isRoutesLayerActive()) return;
        clearTimeout(this._viewportRoutesTimer);
        this._viewportRoutesTimer = setTimeout(() => {
            this.reloadRoutesForViewport().catch((e) => console.warn('[BusCabinet] routes viewport reload', e));
        }, 350);
    }

    async reloadRoutesForViewport() {
        if (!this.map || !this.isRoutesLayerActive()) return;
        if (this.isPinnedRouteFilter()) {
            const hasLayers = this.routeLayerGroup
                && typeof this.routeLayerGroup.getLayers === 'function'
                && this.routeLayerGroup.getLayers().length > 0;
            if (!hasLayers) {
                if (!this._pinnedRouteFeatures.length) await this.ensurePinnedFilterRoutes();
                if (this._pinnedRouteFeatures.length) {
                    this.busData.routes = [...this._pinnedRouteFeatures];
                }
                await this.renderAllRoutes();
            } else {
                this.restyleRoutesForZoom();
            }
            this.refreshViewportStops();
            this.refreshActiveStopPopup();
            return;
        }
        const minZ = this.routesMinZoom();
        const zoom = this.map.getZoom();
        if (zoom < minZ) {
            this.routeLayerGroup?.clearLayers();
            this.activeRouteLayers = {};
            this.busData.routes = [];
            this.setStatus(`請放大至 ${minZ} 級以上顯示路線（現在 ${Math.round(zoom)}）`);
            return;
        }
        if (this._viewportRoutesReloading) {
            this._routesReloadQueued = true;
            return;
        }
        this._viewportRoutesReloading = true;
        this._routesFetchGen = (this._routesFetchGen || 0) + 1;
        try {
            this._pinnedRouteFeatures = [];
            this.busData.routes = [];
            await this.renderAllRoutes();
            this.refreshViewportStops();
            this.refreshActiveStopPopup();
        } finally {
            this._viewportRoutesReloading = false;
            if (this._routesReloadQueued) {
                this._routesReloadQueued = false;
                this.scheduleViewportRoutesReload();
            }
        }
    }

    async fetchStopsForBounds(bounds) {
        if (!bounds) return null;
        const q = this.bboxQuery(bounds);
        const res = await fetch(`/api/live/bus/stops/bbox?${q}`, { credentials: 'include' });
        if (!res.ok) return null;
        return res.json();
    }

    applyStopFeatures(features) {
        this.stopsById.clear();
        for (const f of features || []) {
            const sid = f.properties?.stop_id || f.properties?.id;
            if (sid) this.stopsById.set(String(sid), f);
        }
        this.busData.stops = [...this.stopsById.values()];
    }

    scheduleViewportStopsReload() {
        if (!this.isLoaded) return;
        clearTimeout(this._viewportReloadTimer);
        this._viewportReloadTimer = setTimeout(() => {
            this.reloadStopsForViewport().catch((e) => console.warn('[BusCabinet] viewport reload', e));
        }, 350);
    }

    async reloadStopsForViewport() {
        if (!this.map) return;
        const shadow = this.shadowRoot;
        const toggleStops = shadow?.getElementById('toggleStops');
        if (!this.stopsLayerWanted) {
            if (toggleStops?.checked) toggleStops.checked = false;
            return;
        }
        if (this._viewportReloading) {
            this._stopsReloadQueued = true;
            return;
        }

        this._viewportReloading = true;
        try {
            const zoom = this.map.getZoom();
            if (zoom < BUS_STOPS_MIN_ZOOM) {
                this.stopLayerGroup?.clearLayers();
                this.setStatus(`請放大至 ${BUS_STOPS_MIN_ZOOM} 級以上顯示站牌（現在 ${Math.round(zoom)}）`);
                return;
            }
            const data = await this.fetchStopsForBounds(this.map.getBounds());
            if (data?.features?.length) {
                this.applyStopFeatures(data.features);
                this._lastStopBboxStats = data._stats || null;
            } else {
                this.applyStopFeatures([]);
                this._lastStopBboxStats = data?._stats || null;
            }
            this.refreshViewportStops();
        } finally {
            this._viewportReloading = false;
            if (this._stopsReloadQueued) {
                this._stopsReloadQueued = false;
                this.scheduleViewportStopsReload();
            }
        }
    }

    async loadBusRouteIndex() {
        try {
            const idxRes = await fetch('/api/live/bus/index', { credentials: 'include' });
            if (idxRes.ok) {
                this.busRouteIndex = await idxRes.json();
                this.transportDataUpdatedAt = this.busRouteIndex?.generated_at || null;
                const sg = this.busRouteIndex?.station_groups;
                this.stationGroups = sg
                    ? new Map(Object.entries(sg))
                    : null;
            }
        } catch {
            /* index optional */
        }
    }

    applyDefaultStopLayerOff() {
        this.stopsLayerWanted = false;
        const shadow = this.shadowRoot;
        const toggleStops = shadow?.getElementById('toggleStops');
        const toggleStopLabels = shadow?.getElementById('toggleStopLabels');
        if (toggleStops) toggleStops.checked = false;
        if (toggleStopLabels) {
            toggleStopLabels.checked = false;
            toggleStopLabels.disabled = true;
        }
        this.showStopLabels = false;
        this.stopLayerGroup?.clearLayers?.();
        this.activeStopMarkers = {};
        this.busData.stops = [];
    }

    initControls() {
        const shadow = this.shadowRoot;
        this.applyDefaultStopLayerOff();
        const toggleStops = shadow.getElementById('toggleStops');
        const toggleRoutes = shadow.getElementById('toggleRoutes');
        const toggleStopLabels = shadow.getElementById('toggleStopLabels');
        const toggleRouteLabels = shadow.getElementById('toggleRouteLabels');
        const toggleRouteVariants = shadow.getElementById('toggleRouteVariants');

        if (toggleStops) {
            toggleStops.addEventListener('change', (e) => {
                this.stopsLayerWanted = !!e.target.checked;
                this.updateLabelToggleStates();
                if (e.target.checked) {
                    this.reloadStopsForViewport().catch((err) => console.warn(err));
                } else {
                    this.stopLayerGroup.clearLayers();
                    this.syncStopLabelsVisibility();
                    this.setStatus('站牌已關閉');
                }
            });
        }

        if (toggleRoutes) {
            toggleRoutes.addEventListener('change', (e) => {
                if (this._suppressRouteToggle) return;
                this.updateLabelToggleStates();
                if (e.target.checked) {
                    this.reloadRoutesForViewport().catch((err) => console.warn(err));
                } else {
                    this.routeLayerGroup.clearLayers();
                    this.activeRouteLayers = {};
                    this.busData.routes = [];
                    this.routeFilterMode = 'all';
                    this.routeFilterDetails = null;
                    if (this._activeRoutePopupLayer?.closePopup) {
                        this._activeRoutePopupLayer.closePopup();
                    }
                    this._activeRoutePopupLayer = null;
                    this.syncRouteLabelsVisibility();
                }
            });
        }

        if (toggleStopLabels) {
            toggleStopLabels.addEventListener('change', (e) => {
                this.showStopLabels = e.target.checked;
                this.syncStopLabelsVisibility();
            });
        }

        if (toggleRouteLabels) {
            toggleRouteLabels.addEventListener('change', (e) => {
                this.showRouteLabels = e.target.checked;
                this.syncRouteLabelsVisibility();
            });
        }

        if (toggleRouteVariants) {
            toggleRouteVariants.addEventListener('change', (e) => {
                this.showRouteVariants = e.target.checked;
                if (this.isRoutesLayerActive()) {
                    this.busData.routes = [];
                    this.reloadRoutesForViewport().catch((err) => console.warn(err));
                }
            });
        }

        const searchInput = shadow.getElementById('busRouteSearch');
        if (searchInput) {
            searchInput.addEventListener('focus', () => {
                this.ensureRouteCatalog().catch((err) => console.warn('[BusCabinet] catalog', err));
            });
            searchInput.addEventListener('input', (e) => {
                this.scheduleRouteSearch(e.target.value);
            });
            searchInput.addEventListener('keydown', (e) => {
                this.onRouteSearchKeydown(e);
            });
        }

        this.updateLabelToggleStates();
    }

    async ensureRouteCatalog() {
        if (this._routeCatalog) return this._routeCatalog;
        if (this._routeCatalogLoading) return this._routeCatalogLoading;
        this._routeCatalogLoading = (async () => {
            const res = await fetch('/api/live/bus/routes/catalog', { credentials: 'include' });
            if (!res.ok) throw new Error(`catalog ${res.status}`);
            const data = await res.json();
            this._routeCatalog = data.routes || [];
            return this._routeCatalog;
        })().finally(() => {
            this._routeCatalogLoading = null;
        });
        return this._routeCatalogLoading;
    }

    scheduleRouteSearch(raw) {
        clearTimeout(this._routeSearchTimer);
        this._routeSearchTimer = setTimeout(() => {
            this.runRouteSearch(raw).catch((err) => console.warn('[BusCabinet] search', err));
        }, 140);
    }

    async runRouteSearch(raw) {
        const q = String(raw || '').trim();
        if (!q) {
            this._routeSearchHits = [];
            this._routeSearchActive = -1;
            this.renderRouteSearchHits([]);
            return;
        }
        await this.ensureRouteCatalog();
        const routes = searchBusRoutes(this._routeCatalog, q).map((h) => ({
            ...h,
            kind: 'bus_route',
            kind_zh: '公車'
        }));
        let stops = [];
        try {
            const remote = await searchGisPlaces(q, { kinds: 'bus_stop', limit: 10 });
            stops = (remote.hits || []).map((h) => ({ ...h, kind: 'bus_stop' }));
        } catch {
            /* 目錄未編譯時略過站牌 */
        }
        this._routeSearchHits = [...routes, ...stops];
        this._routeSearchActive = this._routeSearchHits.length ? 0 : -1;
        this.renderRouteSearchHits(this._routeSearchHits);
    }

    renderRouteSearchHits(hits) {
        const box = this.shadowRoot?.getElementById('busRouteSearchHits');
        if (!box) return;
        if (!hits?.length) {
            const q = String(this.shadowRoot.getElementById('busRouteSearch')?.value || '').trim();
            if (!q) {
                box.hidden = true;
                box.innerHTML = '';
                return;
            }
            box.hidden = false;
            box.innerHTML = '<div class="bus-search-empty">沒有符合的路線</div>';
            return;
        }
        box.hidden = false;
        box.innerHTML = hits.map((hit, i) => {
            const ref = this.escapeHtml(hit.ref || hit.name || '');
            const city = this.escapeHtml(hit.city_zh || hit.city || hit.h_city_code || '');
            const kind = this.escapeHtml(hit.kind_zh || '');
            const dep = this.escapeHtml(hit.h_departure || '—');
            const dest = this.escapeHtml(hit.h_destination || '—');
            const color = this.escapeHtml(hit.color || '#76b1ff');
            const active = i === this._routeSearchActive ? ' is-active' : '';
            return `<button type="button" class="bus-search-hit${active}" data-search-i="${i}" style="--hit-color:${color}">
                <div class="bus-search-hit-top">
                    <span class="bus-search-hit-ref">${ref}</span>
                    <span class="bus-search-hit-city">${kind}${kind && city ? ' · ' : ''}${city}</span>
                </div>
                <div class="bus-search-hit-term">${hit.kind === 'bus_stop' ? this.escapeHtml(hit.sub || '') : `${dep} → ${dest}`}</div>
            </button>`;
        }).join('');
        box.querySelectorAll('.bus-search-hit').forEach((btn) => {
            btn.addEventListener('click', () => {
                const i = Number(btn.getAttribute('data-search-i'));
                const hit = this._routeSearchHits[i];
                if (hit) this.selectRouteSearchHit(hit);
            });
        });
        box.querySelector('.bus-search-hit.is-active')?.scrollIntoView({
            block: 'nearest',
            inline: 'nearest'
        });
    }

    onRouteSearchKeydown(e) {
        const hits = this._routeSearchHits;
        if (e.key === 'Escape') {
            this.renderRouteSearchHits([]);
            this.shadowRoot.getElementById('busRouteSearch')?.blur();
            return;
        }
        if (!hits.length) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this._routeSearchActive = (this._routeSearchActive + 1) % hits.length;
            this.renderRouteSearchHits(hits);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this._routeSearchActive = (this._routeSearchActive - 1 + hits.length) % hits.length;
            this.renderRouteSearchHits(hits);
        } else if (e.key === 'Enter' && this._routeSearchActive >= 0) {
            e.preventDefault();
            this.selectRouteSearchHit(hits[this._routeSearchActive]);
        }
    }

    async selectRouteSearchHit(hit) {
        if (!hit) return;
        if (hit.kind === 'bus_stop' && Number.isFinite(Number(hit.lat)) && Number.isFinite(Number(hit.lon))) {
            this.stopsLayerWanted = true;
            const toggleStops = this.shadowRoot?.getElementById('toggleStops');
            if (toggleStops) toggleStops.checked = true;
            this.updateLabelToggleStates();
            this.map?.setView([Number(hit.lat), Number(hit.lon)], 16);
            await this.reloadStopsForViewport();
            this.setStatus(`已定位站牌 ${hit.name || ''}`);
            const box = this.shadowRoot?.getElementById('busRouteSearchHits');
            if (box) box.hidden = true;
            return;
        }
        await this.showOnlyRoute(hit);
        this.fitPinnedRouteBounds();
        const label = `${hit.ref || ''}（${hit.city_zh || hit.h_city_code || ''}）`.trim();
        this.setStatus(`已定位 ${label}`);
        const box = this.shadowRoot?.getElementById('busRouteSearchHits');
        if (box) {
            box.hidden = true;
        }
    }

    fitPinnedRouteBounds() {
        if (!this.map) return;
        const feats = this._pinnedRouteFeatures?.length
            ? this._pinnedRouteFeatures
            : this.busData.routes;
        let minX = 180;
        let minY = 90;
        let maxX = -180;
        let maxY = -90;
        let any = false;
        for (const f of feats || []) {
            const rings = f.geometry?.type === 'LineString'
                ? [f.geometry.coordinates]
                : (f.geometry?.coordinates || []);
            for (const ring of rings) {
                for (const pt of ring || []) {
                    const x = Number(pt[0]);
                    const y = Number(pt[1]);
                    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
                    any = true;
                    minX = Math.min(minX, x);
                    minY = Math.min(minY, y);
                    maxX = Math.max(maxX, x);
                    maxY = Math.max(maxY, y);
                }
            }
        }
        if (!any) return;
        this.map.fitBounds([[minY, minX], [maxY, maxX]], { padding: [48, 48], maxZoom: 14 });
    }

    setStatus(text) {
        const el = this.shadowRoot.getElementById('bus-status');
        if (el) el.innerText = text;
    }

    /**
     * 站點：依地圖視窗 bbox 載入（支援雙北／全台平移縮放）；路線：勾選時依視窗 bbox
     */
    async loadBusNetwork() {
        try {
            this.busData.routes = [];
            this.isLoaded = true;
            this.applyDefaultStopLayerOff();
            this.setStatus('就緒 · 預設不顯示站牌，勾選後才載入');
        } catch (err) {
            console.error('[BusCabinet] Load failed:', err);
            this.setStatus('❌ 數據加載失敗');
        }
    }

    /**
     * 狀態列只報「畫面上的站」；載入／命中數另註，禁止把 API 上限 12000 寫成視窗內站數。
     */
    countRenderedStopMarkers() {
        const g = this.stopLayerGroup;
        if (!g) return 0;
        if (typeof g.getAllChildMarkers === 'function') {
            return g.getAllChildMarkers().length;
        }
        return typeof g.getLayers === 'function' ? g.getLayers().length : 0;
    }

    updateStopLayerStatus() {
        const shown = this.countRenderedStopMarkers();
        const loaded = this.busData.stops?.length || 0;
        const stats = this._lastStopBboxStats || {};
        const hit = stats.totalInBox;
        const capped = !!stats.capped;
        if (!loaded && !shown) {
            this.setStatus('視窗內無站牌');
            return;
        }
        if (capped && Number.isFinite(hit)) {
            this.setStatus(`✅ 顯示 ${shown} 站（視窗命中 ${hit}，載入上限 ${loaded}）`);
            return;
        }
        if (Number.isFinite(hit) && hit !== shown) {
            this.setStatus(`✅ 顯示 ${shown} 站（視窗 ${hit}）`);
            return;
        }
        this.setStatus(`✅ 顯示 ${shown} 站`);
    }

    /**
     * 高畫質視窗自我調節器 (Viewport-Aware Renderer)
     * 僅渲染當前地圖可視範圍內的站牌，避免瀏覽器因渲染數千個 DOM 而卡死
     */
    refreshViewportStops() {
        if (!this.map || !this.isLoaded) return;
        
        const shadow = this.shadowRoot;
        const toggleStops = shadow.getElementById('toggleStops');
        if (!this.stopsLayerWanted || !toggleStops?.checked) return;

        const bounds = this.map.getBounds();
        this.stopLayerGroup.clearLayers();
        this.activeStopMarkers = {};

        // 獲取目前可視範圍內的站牌
        const visibleStops = this.busData.stops.filter((stop) => {
            if (!stop.geometry || !stop.geometry.coordinates) return false;
            const [lng, lat] = stop.geometry.coordinates;
            if (!bounds.contains([lat, lng])) return false;
            if (!this.stopMatchesRouteFilter(stop)) return false;
            return this.stopHasLoadedRouteAssociation(stop);
        });

        // 渲染站牌
        visibleStops.forEach(stop => {
            const [lng, lat] = stop.geometry.coordinates;
            const p = stop.properties;
            
            // DivIcon 進 markerPane（在路線 canvas 之上）。
            // preferCanvas 時 circleMarker 與路線各佔整幅 canvas，上層會吞掉下層觸控。
            const marker = L.marker([lat, lng], {
                icon: busStopDivIcon(false),
                keyboard: false,
                riseOnHover: true,
                zIndexOffset: 200
            });

            this.bindStopPopupInteractions(marker, stop);
            this.bindMapLabel(marker, this.buildStopMapLabelHtml(stop), this.showStopLabels);
            const sid = this.stopId(stop);
            if (sid) this.activeStopMarkers[sid] = marker;
            marker.addTo(this.stopLayerGroup);
        });

        this.applyStopVisibilityFilter();
        this.updateStopLayerStatus();
    }

    reapplyStopHighlights() {
        this.applyStopVisibilityFilter();
    }

    reapplyRouteHighlights() {
        if (!this.highlightedRouteKeys.size) return;
        this.highlightedRouteKeys.forEach((key) => {
            const layer = this.activeRouteLayers[key];
            if (layer) {
                this.applyLayerStyle(layer, { weight: 8, opacity: 1, dashArray: null });
            }
        });
    }

    async onStopClicked(stop) {
        await this.showStopRelatedRoutes(stop);
    }

    async onRouteClicked(route, ev) {
        const p = route.properties || {};
        await this.showOnlyRoute(p, this.activePopupStop || null);
        const layer = this.resolveRouteLayer(p);
        if (layer) this.openRoutePopup(layer, route, ev?.latlng);
        this.blurInteractiveTarget(ev);
    }

    async ensureRoutesLoaded() {
        if (this.isPinnedRouteFilter()) {
            await this.paintPinnedRoutes();
            return;
        }
        const hasLayers = this.routeLayerGroup
            && typeof this.routeLayerGroup.getLayers === 'function'
            && this.routeLayerGroup.getLayers().length > 0;
        if (this.busData.routes.length && hasLayers) return;
        await this.renderAllRoutes();
    }

    /**
     * 繪製所有公車路線
     */
    async renderAllRoutes() {
        if (!this.map || !this.isLoaded) return;

        if (!this.busData.routes.length) {
            if (this.isPinnedRouteFilter()) {
                this.routeLayerGroup?.clearLayers();
                this.activeRouteLayers = {};
                this.setStatus('找不到該路線幾何');
                return;
            }
            const bounds = this.map.getBounds();
            const q = this.bboxQuery(bounds);
            if (this.showRouteVariants) q.set('variants', '1');
            try {
                const gen = this._routesFetchGen;
                this.setStatus('載入視窗路線…');
                const res = await fetch(`/api/live/bus/routes/bbox?${q}`, { credentials: 'include' });
                if (gen !== this._routesFetchGen) return;
                if (res.ok) {
                    const data = await res.json();
                    this.busData.routes = prepareBusRoutesForDisplay(data.features || []);
                    const shown = this.busData.routes.length;
                    const total = data._stats?.totalInBox ?? shown;
                    this.setStatus(
                        data._stats?.capped
                            ? `✅ 視窗內 ${shown} 條路線（共 ${total}，已截斷）`
                            : `✅ 視窗內 ${shown} 條路線`
                    );
                } else {
                    this.setStatus('❌ 路線載入失敗');
                }
            } catch (e) {
                console.warn('[BusCabinet] routes bbox failed', e);
                this.setStatus('❌ 路線載入失敗');
            }
        }

        this.routeLayerGroup.clearLayers();
        this.activeRouteLayers = {};

        this.busData.routes.forEach(route => {
            if (!busRouteHasDrawableGeometry(route)) return;

            const p = route.properties || {};
            const color = this.routeColor(p);
            p.color = color;
            p.h_vehicle_tags = this.vehicleTags(p);
            route.properties = p;

            const lineStyle = this.getRouteLineStyle(p);
            if (!this.map._sovereignLineCanvas && typeof L?.canvas === 'function') {
                this.map._sovereignLineCanvas = L.canvas({
                    padding: 0.5,
                    tolerance: BUS_ROUTE_CANVAS_TOLERANCE
                });
            } else if (this.map._sovereignLineCanvas?.options) {
                this.map._sovereignLineCanvas.options.tolerance = BUS_ROUTE_CANVAS_TOLERANCE;
            }
            const polyline = L.geoJSON(route, {
                style: lineStyle,
                renderer: this.map._sovereignLineCanvas,
                bubblingMouseEvents: false
            });

            polyline.on('mouseover', () => {
                if (this.routeFilterMode !== 'all' && !this.routePropsMatchesFilter(p)) return;
                this.applyLayerStyle(polyline, { color, weight: 7, opacity: 1.0 });
            });
            polyline.on('mouseout', () => {
                if (this.routeFilterMode !== 'all' && !this.routePropsMatchesFilter(p)) return;
                const isHighlighted = [...busRouteStableIds(p)]
                    .some((k) => this.highlightedRouteKeys.has(k));
                if (isHighlighted) return;
                const base = this.getRouteLineStyle(p);
                this.applyLayerStyle(polyline, {
                    ...base,
                    weight: this.routeFilterMode === 'all' ? base.weight : base.weight + 1.5
                });
            });

            L.DomEvent.disableClickPropagation(polyline);
            this.bindMapLabel(polyline, this.buildRouteMapLabelHtml(p), this.showRouteLabels);
            polyline.on('click', (e) => {
                L.DomEvent.stopPropagation(e);
                this.onRouteClicked(route, e);
            });

            polyline.addTo(this.routeLayerGroup);
            this.registerRouteLayer(polyline, route, p);
        });

        this.applyRouteVisibilityFilter();
        this.restyleRoutesForZoom();
        this.reapplyRouteHighlights();
        this.syncMapLabelsVisibility();
        this.refreshViewportStops();
        this.refreshActiveStopPopup();
    }

    /**
     * [v17.5.3] 接收分時圈廣播並高亮交集路線
     */
    handleIsochroneIntersection(data) {
        if (typeof turf === 'undefined' || !data.polygon) return;
        
        // 重設舊有高亮
        this.resetRouteHighlights();

        const isocPolygon = data.polygon;

        this.ensureRoutesLoaded().then(() => {
            this.busData.routes.forEach(route => {
                try {
                    if (turf.booleanIntersects(route, isocPolygon)) {
                        const layer = this.resolveRouteLayer(route.properties);
                        if (layer) {
                            this.applyLayerStyle(layer, {
                                color: this.routeColor(route.properties),
                                weight: 8,
                                opacity: 1.0,
                                dashArray: null
                            });
                        }
                    }
                } catch {
                    /* 忽略非幾何數據 */
                }
            });
        });
    }

    resetRouteHighlights() {
        if (!this.isLoaded) return;
        this.highlightedRouteKeys.clear();
        if (this.routeFilterMode !== 'all') {
            this.applyRouteVisibilityFilter();
            return;
        }
        this.routeLayerGroup?.eachLayer((layer) => {
            const props = layer._busRouteProps || layer.feature?.properties;
            if (!props) return;
            if (typeof layer.setOpacity === 'function') layer.setOpacity(1);
            this.applyLayerStyle(layer, {
                color: this.routeColor(props),
                weight: 4.5,
                opacity: 0.85,
                dashArray: null
            });
        });
    }

    clearAll() {
        this.closeLockedMapPopup();
        clearTimeout(this._labelDeclutterTimer);
        if (this.stopLayerGroup && this.map) this.map.removeLayer(this.stopLayerGroup);
        if (this.routeLayerGroup && this.map) this.map.removeLayer(this.routeLayerGroup);
        this.stopLayerGroup = null;
        this.routeLayerGroup = null;
        this.activeRouteLayers = {};
        this.activeStopMarkers = {};
        this.highlightedRouteKeys.clear();
        this.highlightedStopIds.clear();
        this.routeFilterMode = 'all';
        this._pinnedRouteFeatures = [];
        this._routesFetchGen = (this._routesFetchGen || 0) + 1;
        this.routeFilterDetails = null;
        this.activePopupStop = null;
        this.stopsById.clear();
        clearTimeout(this._viewportReloadTimer);
    }
}

customElements.define('bus-cabinet', BusCabinet);

/**
 * [v17.5.0] 主權熱插拔註冊機制（支援靜態與動態雙重組裝）
 */
function dock() {
    if (window.appCore) {
        // 防止重複註冊以避免 double-instance bug
        if (window.appCore.getCabinet('bus')) {
            return;
        }

        const staticInstance = document.getElementById('bus-view') || document.querySelector('bus-cabinet');
        if (staticInstance) {
            window.appCore.registerCabinet('bus', staticInstance);
        } else {
            const instance = document.createElement('bus-cabinet');
            const container = document.getElementById('socket-bus');
            if (container) {
                container.innerHTML = '';
                container.appendChild(instance);
                window.appCore.registerCabinet('bus', instance);
            }
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', dock);
} else {
    dock();
}
